import React from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';

import { getSortingCallback } from 'utils/common';
import { IExtendedColumn } from 'utils/grid/types';
import { notificationService } from 'utils/notificationService';
import { IOrdersType, ISortingCallback, TSortDirection } from 'utils/types';

import { useDataGridContext } from '../../DataGridContext';

const SORTING_ORDER = [null, 'ASC', 'DESC'];

interface IUseGridColumnSort<T> {
  cellData: IExtendedColumn<T>;
  cellTitle: string;
  isSortable: boolean;
  onClick: (e: React.MouseEvent) => void;
  sortedColumn?: IOrdersType;
  sortIsOn: boolean;
}

interface IUseGridColumnSortProps<T> {
  columns: IExtendedColumn<T>[];
  index: number;
}

const useGridColumnSort = <T>({
  columns,
  index,
}: IUseGridColumnSortProps<T>): IUseGridColumnSort<T> => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.sorting' });

  const {
    defaultSortedColumn,
    filters,
    isDefaultGridSorting,
    onSort,
    requestedSort,
    sort,
  } = useDataGridContext();

  const defaultSorted = React.useMemo(
    () => columns.find((item) => item.dataKey === defaultSortedColumn),
    [columns, defaultSortedColumn],
  );

  const sortingProps = {
    callback: onSort,
    columns,
    filters,
    index,
    isDefaultGridSorting,
    sort,
  } as ISortingCallback<T>;
  const { onSortingChange, sortedColumn } = getSortingCallback(sortingProps);

  const cellData = columns?.[index];
  const cellTitle = cellData?.title || '';

  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const dataKey = cellData?.dataKey as string;

      const requestedDirection = requestedSort?.[dataKey]?.value as
        | TSortDirection
        | undefined;

      // The server echoes its own default order even when nothing was requested,
      // so it is only a trustworthy "current step" for default-sorted columns.
      const trustServerOrder = isDefaultGridSorting || dataKey === defaultSortedColumn;

      const serverDirection =
        sortedColumn?.property === dataKey ? sortedColumn?.direction : undefined;

      const currentDirection =
        requestedDirection ?? (trustServerOrder ? serverDirection : undefined);

      const currentSortIndex = SORTING_ORDER.indexOf(currentDirection ?? null);

      const ascIndex = SORTING_ORDER.indexOf('ASC');

      let nextIndex =
        dataKey === defaultSortedColumn && currentDirection === 'DESC'
          ? ascIndex
          : currentSortIndex + 1;

      if (nextIndex > SORTING_ORDER.length - 1) {
        nextIndex = 0;
      }

      const nextItem = SORTING_ORDER[nextIndex] as TSortDirection;

      const config = nextItem
        ? { direction: nextItem, property: cellData?.dataKey as string }
        : null;

      onSortingChange?.(config);

      const defaultText = defaultSorted
        ? `${t('off')} ${defaultSorted.title.toLowerCase()}`
        : t('defaultSortText');

      notificationService.show({
        title: nextItem
          ? `${t('order')} ${cellTitle.toLowerCase()} ${t('changed')} ${
              nextItem === 'ASC' ? t('ascending') : t('descending')
            }`
          : defaultText,
        type: NOTIF_TYPES.INFO,
      });
    },
    [
      sortedColumn?.property,
      sortedColumn?.direction,
      cellData?.dataKey,
      defaultSortedColumn,
      isDefaultGridSorting,
      requestedSort,
      onSortingChange,
      defaultSorted,
      t,
      cellTitle,
    ],
  );

  const sortIsOn = !!sortedColumn?.direction;

  return {
    cellData,
    cellTitle,
    isSortable: !!onSortingChange,
    onClick,
    sortedColumn,
    sortIsOn,
  };
};

export default useGridColumnSort;
